import React, { useEffect, useState } from 'react';
import { Formik, Form } from 'formik';
import moment from 'moment';
import lodashCompact from 'lodash/fp/compact';
import lodashGroupBy from 'lodash/fp/groupBy';

import {
  INCOME_VERIFICATION_STATUS_COMPLETED,
  INCOME_VERIFICATION_STATUS_PENDING,
} from 'common/incomeVerificationConstants';

import { RENTAL_APPLICATION_STEP_ADDITIONAL_INFO } from 'common/householdApplication/rentalApplicationHelpers';

import {
  SR_APPLICATION_ASSEMBLY_LINE_STATUS_IN_PROGRESS,
  SR_APPLICATION_ASSEMBLY_LINE_STEP_ID_MOVE_IN_DATE,
  SR_APPLICATION_ASSEMBLY_LINE_STEP_ID_INCOME_VERIFICATION,
} from 'common/applicationAssemblyLineConstants';

import setNullEmptyValues from 'common/util/setNullEmptyValues';
import momentWithAustinTimezone from 'common/util/momentWithAustinTimezone';

import { getListingAvailableDateRange } from 'common/listingHelpers';

import getApplicationFormFields, {
  APPLICATION_FORM_TYPE_SR_APPLICATION,
  srApplicationFormFieldgroupToAssemblyLineStepIdMapping,
} from 'common/householdApplication/applicationFormFields';
import { getHouseholdApplicationDataFields } from 'common/householdApplication/applicationHelpers';

import { getInitialAssemblyLineVOIEFormState } from 'common/householdApplication/incomeHelpers';

import {
  createInitialValues,
  createFormValidationFn,
  createFormFieldsByIdGetter,
} from 'client/helpers/formikHelpers';

import { getStepStatuses } from 'client/components/SrApplicationAssemblyLine/assemblyLineStepIds';

import SrBox from 'client/components/SrBox';
import SrText from 'client/components/SrText';
import SrErrorMessage, {
  SR_ERROR_MESSAGE_THEME_ORANGE,
} from 'client/components/SrErrorMessage/SrErrorMessage';
import SrFormFieldGroup from 'client/components/SrFormFieldGroup';
import SrStaticFooter from 'client/components/SrStaticFooter';
import ButtonDeprecated from 'client/components/ButtonDeprecated';
import SrVerifyIncomeOptions from 'client/components/SrVerifyIncomeOptions/SrVerifyIncomeOptions';
import SrRentalApplicationFlowStepHeader from 'client/components/SrRentalApplicationFlowStepHeader';
import {
  getFormFields as getIncomeVerificationFormFields,
  getStepErrors as getIncomeVerificationFormFieldErrors,
} from 'client/components/SrApplicationAssemblyLine/SrApplicationAssemblyLineStepIncomeVerification/SrApplicationAssemblyLineStepIncomeVerification'; // eslint-disable-line max-len

import useBranch from 'client/hooks/useBranch';
import HouseholdActions from 'client/actions/Household';
import AssemblyLineActions from 'client/actions/AssemblyLine';
import IncomeVerificationActions from 'client/actions/IncomeVerification';

const SrRentalApplicationFlowStepAdditionalInformation = ({
  user,
  householdListing,
  listing,
  tenantScreeningCriteria,
  householdId,
  householdApplicationId,
  guarantorApplicationId,
  household,
  partner,
  incomeVerification,
  dispatch,
  exitRentalApplication,
}) => {
  const [retryIncomeVerificationFailed, setRetryIncomeVerificationFailed] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { assemblyLine } = useBranch({
    assemblyLine: ['assemblyLine', 'byHouseholdListings', householdListing.id],
  });

  useEffect(() => {
    dispatch(AssemblyLineActions.loadAssemblyLineData, {
      householdListingId: householdListing.id,
      reload: true,
    });
  }, [dispatch, householdListing.id]);

  if (!assemblyLine) {
    return null;
  }

  const householdApplication = [
    ...assemblyLine.householdApplications,
    ...assemblyLine.guarantorApplications,
  ].find(({ userId }) => userId === user.id);

  const stepStatuses = getStepStatuses({
    assemblyLine,
    householdApplication,
  });

  const assemblyLineVoie = getInitialAssemblyLineVOIEFormState(householdApplication);

  const incomeVerificationFormFields = getIncomeVerificationFormFields({
    values: assemblyLineVoie,
    householdApplication,
  });
  const incomeVerificationFormFieldErrors = getIncomeVerificationFormFieldErrors({
    formFields: incomeVerificationFormFields,
    values: assemblyLineVoie,
    assemblyLine,
    householdApplication,
  });
  const incomeVerificationFormFieldErrorsByTitle = lodashGroupBy(
    'title',
    incomeVerificationFormFieldErrors,
  );

  const assemblyLineStepIdsToFix = stepStatuses
    .filter(({ status }) => status === SR_APPLICATION_ASSEMBLY_LINE_STATUS_IN_PROGRESS)
    .map(({ stepId }) => stepId);

  const isLeadRenter = household?.leadRenterId === user.id;

  const listingAvailableDateRange = getListingAvailableDateRange({
    listing,
    tenantScreeningCriteria,
  });
  const startDate = momentWithAustinTimezone(listingAvailableDateRange.startDate).startOf('day');
  const endDate = momentWithAustinTimezone(listingAvailableDateRange.endDate).endOf('day');
  const startDateFormatted = startDate.format('MMM D');
  const endDateFormatted = endDate.format('MMM D');

  const incomeVerificationVOIE =
    incomeVerification?.status === INCOME_VERIFICATION_STATUS_PENDING ||
    incomeVerification?.status === INCOME_VERIFICATION_STATUS_COMPLETED
      ? incomeVerification
      : null;

  const defaultValues = getHouseholdApplicationDataFields({
    ...householdApplication,
    incomeVerificationVOIE,
  });

  const formFieldIdsRequired = assemblyLineStepIdsToFix.reduce((acc, stepId) => {
    const formFieldIds = srApplicationFormFieldgroupToAssemblyLineStepIdMapping[stepId];
    return [...acc, ...formFieldIds];
  }, []);

  const getFormFields = ({
    formValues,
    showMoveInDateWarning,
    deleteAdditionalProofOfIncomeFile,
  }) => {
    const formFields = getApplicationFormFields({
      formType: APPLICATION_FORM_TYPE_SR_APPLICATION,
      formValues: formValues,
      listing,
      tenantScreeningCriteria,
      isLeadRenter,
      uploadAs: user.id,
      incomeVerificationStatus: incomeVerification?.status,
      incomeVerificationProviderEnabled: partner.incomeVerificationProviderEnabled,
      moveInWarningText: showMoveInDateWarning
        ? 'The landlord may be less likely to approve your application if you choose a move-in ' +
          `date outside of their desired timeframe (${startDateFormatted} - ${endDateFormatted}).`
        : null,
      deleteAdditionalProofOfIncomeFile,
    });

    const formFieldsRequired = lodashCompact(
      formFieldIdsRequired.reduce((memo, fieldId) => {
        memo.push(
          ...formFields.filter(({ id }) => {
            const idWithoutIndexes = id.replace(/\[[^\]]*]/g, '');
            const baseId = idWithoutIndexes.split('.')[0];
            return fieldId === idWithoutIndexes || fieldId === baseId;
          }),
        );
        return memo;
      }, []),
    );

    return formFieldsRequired;
  };

  const retryIncomeVerification = (retryIncomeVerificationFailed = true) => {
    setRetryIncomeVerificationFailed(retryIncomeVerificationFailed);
  };

  const _reloadIncomeVerificationData = async (formikBag) => {
    const incomeResponse = await dispatch(IncomeVerificationActions.getByApplicationId, {
      householdApplicationId: householdApplicationId || guarantorApplicationId,
      reload: true,
      includeCurrentJobInfo: true,
    });

    if (
      incomeResponse?.status === INCOME_VERIFICATION_STATUS_PENDING ||
      incomeResponse?.status === INCOME_VERIFICATION_STATUS_COMPLETED
    ) {
      formikBag.setFieldValue('incomeVerificationVOIE', incomeResponse);

      if (
        incomeResponse &&
        incomeResponse.status === INCOME_VERIFICATION_STATUS_COMPLETED &&
        !formikBag.values.currentJobStartDate
      ) {
        const currentJobStartDate = moment(incomeResponse.currentJobStartDate).toISOString();
        formikBag.setFieldValue('currentJobStartDate', currentJobStartDate);
      }
    }
  };

  const _onDeleteAdditionalProofOfIncomeFile = (formikBag) => {
    return (fileIndex) => {
      const additionalProofOfIncomePhotos = formikBag.values.additionalProofOfIncomePhotos.filter(
        (fileName, index) => index !== fileIndex,
      );

      formikBag.setFieldValue('additionalProofOfIncomePhotos', additionalProofOfIncomePhotos);
      formikBag.setFieldValue(
        'additionalProofOfIncomePhotosCount',
        formikBag.values.additionalProofOfIncomePhotosCount - 1,
      );
    };
  };

  const onSubmitForm = (formValues) => {
    setIsSubmitting(true);

    const updates = {
      ...formValues,
    };

    if ('paystubPhotos' in updates) {
      updates.paystubPhotos = lodashCompact(updates.paystubPhotos);
    }

    if ('bankStatementFileUrls' in updates) {
      updates.bankStatementFileUrls = lodashCompact(updates.bankStatementFileUrls);
    }

    if ('additionalProofOfIncomePhotos' in updates) {
      updates.additionalProofOfIncomePhotos = lodashCompact(updates.additionalProofOfIncomePhotos);
    }

    delete updates.incomeVerificationVOIE;

    dispatch(HouseholdActions.updateRenterProfile, {
      userId: user.id,
      updates: setNullEmptyValues(updates),
    })
      .then((renterProfile) => {
        if (householdApplicationId) {
          return dispatch(HouseholdActions.submitHouseholdApplication, {
            userId: user.id,
            listingId: listing.id,
            householdApplication: renterProfile,
          });
        }
        if (guarantorApplicationId) {
          return dispatch(HouseholdActions.submitGuarantorApplication, {
            userId: user.id,
            listingId: listing.id,
            householdId: householdId,
            guarantorApplication: renterProfile,
          });
        }
      })
      .then(() => {
        exitRentalApplication();
      });
  };

  return (
    <Formik
      initialValues={createInitialValues({
        formFields: getFormFields({
          formValues: defaultValues,
        }),
        defaultValues,
      })}
      validate={(values) => {
        const formFields = getFormFields({
          formValues: values,
        });
        const errors = createFormValidationFn(formFields)(values);
        return errors;
      }}
      onSubmit={onSubmitForm}
      validateOnChange
      render={(formikBag) => {
        const moveInDateMoment = formikBag.values.moveInDate
          ? momentWithAustinTimezone(formikBag.values.moveInDate).startOf('day')
          : null;

        const formFields = getFormFields({
          formValues: formikBag.values,
          showMoveInDateWarning:
            moveInDateMoment &&
            (moveInDateMoment.isBefore(startDate) || moveInDateMoment.isAfter(endDate)),
          deleteAdditionalProofOfIncomeFile: _onDeleteAdditionalProofOfIncomeFile(formikBag),
        });

        const getFormFieldsById = createFormFieldsByIdGetter(formFields);
        const formFieldErrors = createFormValidationFn(formFields)(formikBag.values);

        return (
          <div className="RentalApplicationPage">
            <SrRentalApplicationFlowStepHeader
              isGuarantorApp={guarantorApplicationId && !householdApplicationId}
              activeStep={0}
              hideSteps
            />
            <div className="main">
              <Form className="mainContent">
                {assemblyLineStepIdsToFix.includes(
                  SR_APPLICATION_ASSEMBLY_LINE_STEP_ID_MOVE_IN_DATE,
                ) && (
                  <>
                    <SrFormFieldGroup
                      title="move terms"
                      label={`(landlord prefers move-ins between ${startDateFormatted} - ${endDateFormatted})`}
                      formFields={getFormFieldsById(['moveInDate'])}
                      formikBag={formikBag}
                    />
                  </>
                )}
                {assemblyLineStepIdsToFix.includes(
                  SR_APPLICATION_ASSEMBLY_LINE_STEP_ID_INCOME_VERIFICATION,
                ) && ( // eslint-disable-line max-len
                  <>
                    <SrBox className="SrFormFieldGroup">
                      <SrText className="SrFormFieldGroupTitle">verify income</SrText>
                    </SrBox>
                    {incomeVerificationFormFieldErrors.length > 0 && (
                      <SrBox>
                        {Object.keys(incomeVerificationFormFieldErrorsByTitle).map((title) => (
                          <SrBox padding="10px 0" key={title}>
                            <SrErrorMessage
                              theme={SR_ERROR_MESSAGE_THEME_ORANGE}
                              fontFamily="Whitney"
                            >
                              {title}:
                              <br />
                              {incomeVerificationFormFieldErrorsByTitle[title].map((stepError) => (
                                <div
                                  key={`${stepError.title}-${stepError.fieldId}-${stepError.message}`}
                                >
                                  {stepError.message}
                                  <br />
                                </div>
                              ))}
                            </SrErrorMessage>
                          </SrBox>
                        ))}
                      </SrBox>
                    )}
                    <SrVerifyIncomeOptions
                      dispatch={dispatch}
                      currentUser={user}
                      listing={listing}
                      incomeVerification={incomeVerification}
                      householdApplicationId={householdApplicationId || guarantorApplicationId}
                      retryIncomeVerificationFailed={retryIncomeVerificationFailed}
                      retryIncomeVerification={retryIncomeVerification}
                      reloadIncomeVerificationData={() => _reloadIncomeVerificationData(formikBag)}
                      formFieldErrors={formFieldErrors}
                      formikBag={formikBag}
                      formFields={formFields}
                      incomeVerificationEnabled={partner.incomeVerificationProviderEnabled}
                      rentalApplicationStep={RENTAL_APPLICATION_STEP_ADDITIONAL_INFO}
                      incomeVerificationFormFieldErrors={incomeVerificationFormFieldErrors}
                    />
                  </>
                )}

                <SrStaticFooter size="135-105">
                  <ButtonDeprecated disabled={isSubmitting} onClick={formikBag.handleSubmit}>
                    submit
                  </ButtonDeprecated>
                </SrStaticFooter>
              </Form>
            </div>
          </div>
        );
      }}
    />
  );
};

SrRentalApplicationFlowStepAdditionalInformation.displayName =
  'SrRentalApplicationFlowStepAdditionalInformation';

export default SrRentalApplicationFlowStepAdditionalInformation;
